import { motion, useScroll, useTransform, useSpring } from "framer-motion";
import { Hammer } from "lucide-react";

// Each hammer has: position, size, color, fixed tilt offset, opacity, and its own
// float animation timing so the set feels organic rather than uniform.
const HAMMERS = [
  { top: "4%", left: "6%", size: 26, color: "#16a34a", tilt: 12, op: 0.22, dur: 4.2, delay: 0 },
  { top: "8%", right: "9%", size: 20, color: "#1c1917", tilt: -35, op: 0.12, dur: 5.1, delay: 0.3 },
  { top: "15%", left: "52%", size: 24, color: "#16a34a", tilt: 55, op: 0.18, dur: 3.6, delay: 0.7 },
  { top: "22%", left: "10%", size: 30, color: "#1c1917", tilt: -18, op: 0.14, dur: 4.8, delay: 0.2 },
  { top: "28%", right: "5%", size: 22, color: "#16a34a", tilt: 30, op: 0.2, dur: 5.4, delay: 1.1 },
  { top: "34%", left: "36%", size: 18, color: "#1c1917", tilt: 70, op: 0.1, dur: 3.9, delay: 0.5 },
  { top: "41%", left: "4%", size: 32, color: "#16a34a", tilt: -45, op: 0.18, dur: 4.5, delay: 0.9 },
  { top: "47%", right: "12%", size: 24, color: "#1c1917", tilt: 22, op: 0.14, dur: 5.7, delay: 0.1 },
  { top: "53%", left: "58%", size: 20, color: "#16a34a", tilt: -28, op: 0.2, dur: 4.0, delay: 0.8 },
  { top: "59%", right: "3%", size: 28, color: "#1c1917", tilt: 40, op: 0.12, dur: 6.1, delay: 0.4 },
  { top: "65%", left: "8%", size: 22, color: "#16a34a", tilt: -55, op: 0.22, dur: 3.8, delay: 1.0 },
  { top: "71%", left: "44%", size: 26, color: "#1c1917", tilt: 15, op: 0.13, dur: 5.0, delay: 0.6 },
  { top: "77%", right: "7%", size: 20, color: "#16a34a", tilt: -32, op: 0.2, dur: 4.4, delay: 0.2 },
  { top: "83%", left: "14%", size: 30, color: "#1c1917", tilt: 48, op: 0.14, dur: 5.8, delay: 1.2 },
  { top: "89%", left: "66%", size: 24, color: "#16a34a", tilt: -14, op: 0.2, dur: 4.1, delay: 0.4 },
  { top: "94%", right: "11%", size: 18, color: "#1c1917", tilt: 58, op: 0.12, dur: 3.5, delay: 0.9 },
  { top: "97%", left: "28%", size: 22, color: "#16a34a", tilt: -40, op: 0.18, dur: 5.2, delay: 0.1 },
];

export default function HammersBackdrop({ density = "full" }) {
  const { scrollY } = useScroll();
  // Same rotation curve as the main hero logo so they turn in sync with scroll
  const rotateRaw = useTransform(scrollY, [0, 1200], [-12, 360]);
  const rotate = useSpring(rotateRaw, { mass: 2, stiffness: 50, damping: 22 });

  // "light" density uses every other hammer for subtler coverage in sub-sections
  const hammers = density === "light" ? HAMMERS.filter((_, i) => i % 2 === 0) : HAMMERS;

  return (
    <div
      className="absolute inset-0 overflow-hidden pointer-events-none z-0"
      aria-hidden="true"
      data-testid="hammers-backdrop"
    >
      {hammers.map((h, i) => (
        <motion.div
          key={i}
          style={{
            position: "absolute",
            top: h.top,
            left: h.left,
            right: h.right,
            rotate,
            willChange: "transform",
          }}
        >
          <motion.div
            animate={{
              y: [0, -6, 0, 4, 0],
              opacity: [h.op, h.op * 1.35, h.op, h.op * 1.15, h.op],
            }}
            transition={{
              duration: h.dur,
              delay: h.delay,
              repeat: Infinity,
              ease: "easeInOut",
            }}
            style={{ transform: `rotate(${h.tilt}deg)` }}
          >
            <Hammer
              size={h.size}
              color={h.color}
              strokeWidth={2.4}
              absoluteStrokeWidth
            />
          </motion.div>
        </motion.div>
      ))}
    </div>
  );
}
